import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { ManualBankAccountFields } from "components/SensitiveData/ManualBankAccountFields";
import { SelectAutofillCombobox } from "components/SelectAutofillCombobox";
import { SensitiveDataFieldError } from "components/SensitiveData/SensitiveDataFieldError";
import {
  SENSITIVE_DATA_BANK_ENTRY_MODE,
  SENSITIVE_DATA_BANK_ENTRY_MODE_KEY,
  SENSITIVE_DATA_SAVED_BANK_HINT,
} from "constants/sensitiveData";
import { useFormikContext } from "formik";

type SensitiveDataBankFieldProps = {
  name: string;
  label: string;
  entityId: string;
  groupId: string;
  placeholder?: string;
  required?: boolean;
  isEditingLinkedBank?: boolean;
};

export const SensitiveDataBankField = ({
  name,
  label,
  entityId,
  groupId,
  placeholder,
  required,
  isEditingLinkedBank = false,
}: SensitiveDataBankFieldProps) => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<Record<string, string>>();

  const mode =
    values[SENSITIVE_DATA_BANK_ENTRY_MODE_KEY] ||
    SENSITIVE_DATA_BANK_ENTRY_MODE.SAVED;
  const isManual = mode === SENSITIVE_DATA_BANK_ENTRY_MODE.MANUAL;

  const handleBankSelect = (bankUuid: string) => {
    setFieldTouched(name, true, false);
    setFieldValue(name, bankUuid);
  };

  const handleModeChange = (nextMode: string) => {
    if (!nextMode) return;

    setFieldValue(SENSITIVE_DATA_BANK_ENTRY_MODE_KEY, nextMode);
  };

  if (isEditingLinkedBank) {
    return <ManualBankAccountFields entityId={entityId} groupId={groupId} />;
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <ToggleGroup
        className="t-w-full"
        defaultValue={mode}
        onValueChange={handleModeChange}
      >
        <ToggleGroupItem
          className="t-flex-1"
          value={SENSITIVE_DATA_BANK_ENTRY_MODE.SAVED}
        >
          Select a saved bank
        </ToggleGroupItem>
        <ToggleGroupItem
          className="t-flex-1"
          value={SENSITIVE_DATA_BANK_ENTRY_MODE.MANUAL}
        >
          Enter details manually
        </ToggleGroupItem>
      </ToggleGroup>

      {isManual ? (
        <ManualBankAccountFields entityId={entityId} groupId={groupId} />
      ) : (
        <div className="t-flex t-flex-col t-gap-1.5">
          <SelectAutofillCombobox
            type="bank_accounts"
            entityId={entityId}
            groupId={groupId}
            name={name}
            label={label}
            placeholder={placeholder}
            required={required}
            selected={values[name]}
            onChange={handleBankSelect}
            canAddMore={false}
          />

          <SensitiveDataFieldError name={name} />
          <HelperText>{SENSITIVE_DATA_SAVED_BANK_HINT}</HelperText>
        </div>
      )}
    </div>
  );
};
